import { Form, Input, Row, Col, Select, Button, Space, Divider, DatePicker, Checkbox, InputNumber, Tooltip, Tag, Modal } from 'antd';
import { useRef, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import WebEditUniversal, { WebEditUniversalProps } from '../../system/WebEditUniversal';
import getList from '../../../utilsTsx/universal/getList';
import fetchApi from '../../../utilsTsx/components/system/fetchApi';
import { getService } from '../../system/WebUniversal';
import antdMessage from '../../../utilsTsx/components/system/antdMessage';
import { getMembershipLevelData } from './MembershipRechargePlansEdit';

/** 
 * 🔥 功能开关：是否允许使用手机号查询
 * 暂时关闭设置为 false（只显示会员号查询）
 * 如果将来需要开启手机号查询，将此值改为 true 即可
 */
const ENABLE_PHONE_QUERY = false;

/** 分割线统一标题样式（antd 6.x 用 styles） */
const dividerTitleStyles = {
    content: { color: '#1677ff', fontWeight: 600 },
} as const;

/** 重要字段的红色 label 样式 */
const importantLabelStyle = { color: '#ff4d4f', fontWeight: 600 } as const;

/**
 * 第三步需要清空的字段
 * 说明：切换绑定方式（重回第一步）时，这些字段都属于查询后的结果 / 用户填写内容，
 *       都应清空，避免沿用上一次的旧数据造成脏数据提交。
 */
const stepThreeFields: string[] = [
    'membershipTypeId',
    'membershipLevelId',
    'verificationMethodId',
    'membershipName',
    'appellationId',
    'password',
    'gender',
    'contactNumber',
    'phoneNumber',
    'merchantId',
    'dateOfBirth',
    'idNumber',
    'nationality',
    'ethnicity',
    'shopId',
    'companyName',
    'balance',
    'gifts',
    'address',
    'postalCode',
    'tags',
    'memo',
    'accountId',
    'isQueryOldSystemSuccess',
    'membershipId' // 确保清理会员号
];

const EditComponent = (props: any) => {
    /** 接口标识 */
    const apiId = 'membership';
    const [form] = Form.useForm();
    const ref = useRef<any>(undefined);

    /**
     * 使用 Modal.useModal() 获取 modal 实例：
     * - 静态 Modal.confirm 不会继承 ConfigProvider 的主题（暗色主题下仍固定白底）
     * - useModal 返回的 modal.confirm 会继承当前主题，背景色随主题（黑色 / 白色）
     * - contextHolder 必须渲染到 JSX 中，modal.confirm 才能拿到上下文
     */
    const [modal, contextHolder] = Modal.useModal();

    /** 默认业务服务 */
    const defaultService = getService('business');

    /** 绑定方式选项：bound=关联手机会员 / unbound=不关联手机会员 */
    const [bindMethodData] = useState<any>([
        { id: 'bound', name: '关联手机会员' },
        { id: 'unbound', name: '不关联手机会员' }
    ]);

    /** 当选择“不关联手机会员”时，会员号不可编辑；反之可编辑 */
    const [boundDisabled, setBoundDisabled] = useState<boolean>(true);

    /** 是否已经查询成功：成功后锁定第二步输入，并解锁第三步资料 */
    const [queried, setQueried] = useState<boolean>(false);

    /** 与 queried 同步的 ref，避免 footer 闭包读到旧值 */
    const queriedRef = useRef<boolean>(false);

    /** 同时更新 state 和 ref，避免闭包问题 */
    const updateQueried = (val: boolean) => {
        queriedRef.current = val;
        setQueried(val);
    };

    /** 查询按钮 loading */
    const [querying, setQuerying] = useState<boolean>(false);

    /**
     * 查询结果标识：
     * - true  ：旧系统转入
     * - false ：新卡（旧系统中查不到资料）
     * - undefined：未查询 / 已重置
     */
    const [isOldSystemData, setIsOldSystemData] = useState<boolean | undefined>(undefined);

    /**
     * 查询失败信息：
     * - 有值：第二步下方显示红色错误文案（对齐“会员号”）
     * - undefined：不显示
     */
    const [queryErrorMsg, setQueryErrorMsg] = useState<string | undefined>(undefined);

    /**
     * 是否为“关联手机会员”且查询成功的状态标识：
     * - true：在第二步下方左侧显示绿色“查询到会员”
     * - false：不显示
     */
    const [queriedAsBound, setQueriedAsBound] = useState<boolean>(false);

    // 👇 新增：存储查询到的会员姓名，用于在“查询到会员”后展示
    const [queriedMemberName, setQueriedMemberName] = useState<string | undefined>(undefined);

    /** 各类下拉数据源 */
    const [membershipTypeData, setMembershipTypeData] = useState<any>();
    const [membershipLevelData, setMembershipLevelData] = useState<any>();
    const [appellationData, setAppellationData] = useState<any>();
    const [verificationMethodData, setVerificationMethodData] = useState<any>();
    const [merchantData, setMerchantData] = useState<any>();
    const [shopData, setShopData] = useState<any>();

    useEffect(() => {
        // 会员类型列表
        fetchApi.GET(defaultService, {
            query: {
                apiId: 'membershipType',
                apiExtend: 'showList'
            }
        }).then(res => {
            if (res.success) {
                setMembershipTypeData(res.data);
            }
        });

        // 称呼 / 验证方法（一次请求拿两个字典）
        fetchApi.GET(getService('bm'), {
            query: {
                apiId: 'generalData',
                apiVariable: 'appellation@verificationMethod',
                apiExtend: 'showLists'
            }
        }).then(res => {
            if (res.success) {
                const { appellation, verificationMethod } = res.data;
                setAppellationData(appellation);
                setVerificationMethodData(verificationMethod);
            }
        });

        // 商户列表
        fetchApi.GET(getService('bm'), {
            query: {
                apiId: 'merchant',
                apiExtend: 'showList'
            }
        }).then(res => {
            if (res.success) {
                setMerchantData(res.data);
            }
        });

        // 店铺列表
        fetchApi.GET(getService('bm'), {
            query: {
                apiId: 'shop',
                apiExtend: 'showList'
            }
        }).then(res => {
            if (res.success) {
                setShopData(res.data);
            }
        });
    }, []);

    /**
     * 真正提交“绑定实体卡”
     * 抽成独立函数，便于在“旧系统转入二次确认”后复用
     * 成功后通过 props.closeModal({ success: true }) 带参关闭，
     * 父组件据此刷新列表
     */
    const submitBind = (values: any) => {
        fetchApi.POST(defaultService, {
            body: {
                apiId,
                apiExtend: 'bindPhysicalCard',
                apiData: {
                    ...values,
                    // 余额 / 赠送 空值时兜底为 0
                    balance: values.balance ?? 0,
                    gifts: values.gifts ?? 0,
                    // 生日统一转字符串，避免提交 dayjs 对象
                    dateOfBirth: values.dateOfBirth
                        ? dayjs(values.dateOfBirth).format('YYYY-MM-DD')
                        : undefined
                }
            }
        }).then(res => {
            if (res.success) {
                antdMessage('success', '会员实体卡绑定成功');
                // 关键：带 success 标识关闭，父组件据此刷新列表
                props.closeModal?.({ success: true });
            } else {
                antdMessage('error', '会员实体卡绑定失败', { description: res.message });
            }
        });
    };

    /**
     * 会员实体卡绑定：校验全表单后提交
     * 若为“旧系统转入”（isQueryOldSystemSuccess === true），提交前先弹确认框：
     * - 展示：旧系统转入 + 余额 + 赠送
     * - 点“确定”才继续绑定；点“取消”则什么都不做
     * 注意：这里用 modal.confirm（来自 Modal.useModal），背景色会跟随主题，
     *       暗色主题下自动是深色底，浅色主题下自动是白色底，不再固定白底。
     */
    const bindPhysicalCard = () => {
        // 未查询之前不允许提交，避免触发一堆无意义的红色校验
        // 注意：这里读 queriedRef.current，而不是闭包里的 queried
        if (!queriedRef.current) {
            antdMessage('warning', '请先完成第二步的查询操作');
            return;
        }

        form.validateFields()
            .then((values: any) => {
                // 旧系统转入：提交前弹确认框，展示余额与赠送，确定才继续
                if (values.isQueryOldSystemSuccess === true) {
                    modal.confirm({
                        title: '确认绑定',
                        content: (
                            <div style={{ lineHeight: 1.9 }}>
                                <div style={{ marginBottom: 4 }}>
                                    该会员资料
                                    <Tag color="blue" style={{ margin: '0 4px' }}>
                                        旧系统转入
                                    </Tag>
                                    ，请确认余额与赠送无误：
                                </div>
                                <div>
                                    余额：
                                    <span style={importantLabelStyle}>{values.balance ?? 0}</span>
                                </div>
                                <div>
                                    赠送：
                                    <span style={importantLabelStyle}>{values.gifts ?? 0}</span>
                                </div>
                                <div style={{ marginTop: 8 }}>绑定后，旧系统余额与赠送将会清零，确定继续绑定吗？</div>
                            </div>
                        ),
                        okText: '确定',
                        cancelText: '取消',
                        onOk: () => {
                            // 点“确定”才真正提交
                            submitBind(values);
                        }
                        // 点“取消”不做任何处理，Modal 自动关闭，停留在当前表单
                    });
                    return;
                }

                // 新卡：直接提交
                submitBind(values);
            })
            .catch(() => {
                // 校验失败时 antd 会自动定位到第一个错误字段，这里无需额外处理
            });
    };

    /**
     * 清空第三步所有字段（值 + 校验错误）
     * 用途：切换绑定方式（重回第一步）时调用，避免沿用上一次的旧数据
     */
    const clearStepThree = () => {
        // 声明为 any[]，避免 TS 将 value 类型推断为严格的 undefined 导致类型冲突
        const fieldsToClear: any[] = stepThreeFields.map((name) => ({
            name,
            // 置空值
            value: undefined,
            // 清空校验错误
            errors: []
        }));

        // 切换时，查询方式恢复为“会员号”
        fieldsToClear.push({
            name: 'queryType',
            value: 'membershipId',
            errors: []
        });

        form.setFields(fieldsToClear);
    };

    /**
     * 切换绑定方式时：
     * - 不关联手机号：清空会员号，重置查询状态
     * - 关联手机号：允许填写会员号，并清掉之前“不关联”时产生的校验错误
     * - 无论哪种方式：都清空第三步的所有内容（重回第一步即视为重新开始）
     */
    const handleBindMethodChange = (value: string) => {
        // 切换绑定方式即视为需要重新查询
        updateQueried(false);

        // 查询结果标识也一起清空
        setIsOldSystemData(undefined);

        // 清空查询失败提示
        setQueryErrorMsg(undefined);

        // 清空“查询到会员”标识
        setQueriedAsBound(false);

        // 👇 新增：清空查询到的会员姓名
        setQueriedMemberName(undefined);

        if (value === 'unbound') {
            setBoundDisabled(true);
            form.setFieldValue('membershipId', undefined);
        } else {
            setBoundDisabled(false);
            // 从“不关联”切回“关联”时，清空这三个字段的校验错误
            form.setFields([
                { name: 'membershipTypeId', errors: [] },
                { name: 'membershipLevelId', errors: [] },
                { name: 'membershipName', errors: [] }
            ]);
        }

        // 清空实体卡id，避免沿用上一次的卡号
        form.setFieldValue('physicalCardId', undefined);

        // 关键：重回第一步时，清空第三步所有字段（值 + 校验错误）
        clearStepThree();

        // “查询旧系统并转换”恢复默认勾选状态
        form.setFieldValue('isQueryAndChange', true);
    };

    /**
     * 重置：把整个表单恢复到初始状态
     * - 清空所有字段（值 + 校验错误）
     * - 重置查询状态、绑定方式相关状态
     * - 清空会员级别下拉数据（因为级别依赖类型）
     */
    const handleReset = () => {
        // 1. 重置整个表单（含值 + 校验错误）
        form.resetFields();

        // 2. 重置查询状态
        updateQueried(false);

        // 3. 重置“会员号是否可编辑”，回到默认的不可编辑
        setBoundDisabled(true);

        // 4. 清空级别下拉数据（类型被 reset 后，级别也应清空）
        setMembershipLevelData(undefined);

        // 5. 清空“新卡 / 旧系统转入”标识
        setIsOldSystemData(undefined);

        // 6. 清空查询失败提示
        setQueryErrorMsg(undefined);

        // 7. 清空“查询到会员”标识
        setQueriedAsBound(false);

        // 👇 新增：清空查询到的会员姓名
        setQueriedMemberName(undefined);

        // 8. 恢复“查询旧系统并转换”为默认勾选（resetFields 会回到 initialValue，这里再兜底一次）
        form.setFieldValue('isQueryAndChange', true);

        antdMessage('success', '已重置');
    };

    /**
     * 查询会员卡 + 实体卡资料
     * - 关联手机会员：根据 queryType 传入 会员号 或 手机号
     * - 不关联手机会员：只传实体卡id
     */
    const handleQuery = async () => {
        const bindMethodId = form.getFieldValue('bindMethodId');
        const physicalCardId = form.getFieldValue('physicalCardId');
        const isQueryAndChange = form.getFieldValue('isQueryAndChange');
        
        // 获取查询类型（默认使用会员号查询）
        // 如果全局开关关闭了手机号查询，则强制使用 membershipId
        const rawQueryType = form.getFieldValue('queryType') || 'membershipId';
        const queryType = ENABLE_PHONE_QUERY ? rawQueryType : 'membershipId';

        const membershipId = form.getFieldValue('membershipId');
        const contactNumber = form.getFieldValue('contactNumber');

        // 绑定方式必选
        if (!bindMethodId) {
            antdMessage('warning', '请先选择绑定方式');
            return;
        }
        // 实体卡id必填
        if (!physicalCardId) {
            antdMessage('warning', '请先输入实体卡id');
            return;
        }
        // 关联手机模式下，根据选择的类型进行对应的必填校验
        if (!boundDisabled) {
            if (queryType === 'membershipId' && !membershipId) {
                antdMessage('warning', '请先输入会员号');
                return;
            }
            if (queryType === 'contactNumber' && !contactNumber) {
                antdMessage('warning', '请先输入手机号');
                return;
            }
        }

        setQuerying(true);
        // 每次查询前先清空上一次的失败提示
        setQueryErrorMsg(undefined);
        try {
            const res = await fetchApi.GET(defaultService, {
                query: {
                    apiId: 'membership',
                    apiExtend: 'queryPhysicalCard',
                    apiData: {
                        bindMethodId,
                        physicalCardId,
                        // 两个参数都传给后台，后台按需使用
                        membershipId: boundDisabled ? undefined : membershipId,
                        contactNumber: boundDisabled ? undefined : contactNumber,
                        isQueryAndChange
                    }
                }
            });

            // 请求失败
            if (!res.success) {
                antdMessage('error', '查询失败', { description: res.message });

                // 记录查询失败信息，用于在第二步下方用红色字显示
                setQueryErrorMsg(res.message || '查询失败');

                // 失败时保持未查询状态
                updateQueried(false);
                setIsOldSystemData(undefined);
                setQueriedAsBound(false);
                // 👇 新增：失败时清空姓名
                setQueriedMemberName(undefined);
                return;
            }

            // 请求成功且有数据：回填表单
            const data = res.data;
            const info = data?.accountInfo;
            console.log(data);
            const isQueryOldSystemSuccess = res.data?.isQueryOldSystemSuccess;

            // 👇 新增：提取查到的姓名，兼容两种数据结构
            const fetchedName = data?.membership?.membershipInfo?.membershipName || info?.AccountName;
            setQueriedMemberName(fetchedName);

            if (isQueryOldSystemSuccess === true) {
                form.setFieldsValue({
                    isQueryOldSystemSuccess,
                    accountId: data.accountId,
                    membershipName: info.AccountName,
                    gender: info.Sex === true ? 1 : 0,
                    // 如果后台没给手机号，就保留第二步输入的值（防止用手机号查询时被覆盖为空）
                    contactNumber: info.MobileNum || form.getFieldValue('contactNumber'),
                    phoneNumber: info.ContactTel,
                    idNumber: info.IDCard,
                    dateOfBirth: info.Birthday ? dayjs(info.Birthday) : undefined,
                    companyName: info.CompanyName,
                    address: info.Address,
                    postalCode: info.Zips,
                    tags: info.Features,
                    memo: `${isQueryOldSystemSuccess ? '由旧系统转入' : ''} - ${data.account?.Memos ?? ''} - ${info.Memos ?? ''}`,
                    // 新增：余额 / 赠送 回填（空值时兜底为 0）
                    balance: data.balance ?? 0,
                    gifts: data.gifts ?? 0,
                    // 确保会员号也保留在表单中（防止用会员号查询时被覆盖为空）
                    membershipId: data.membershipId || form.getFieldValue('membershipId'),
                });
            }
            updateQueried(true);

            // 记录“查询结果类型”：true=旧系统转入，false=新卡
            setIsOldSystemData(isQueryOldSystemSuccess === true);

            // 关联手机会员查询成功时，显示绿色“查询到会员”
            setQueriedAsBound(bindMethodId === 'bound');

            // 查询成功，清空失败提示
            setQueryErrorMsg(undefined);

            if (isQueryOldSystemSuccess === true) {
                antdMessage('success', '查询成功');
            } else {
                antdMessage('info', '已查询,旧系统查不到相关资料,可以绑定为新卡');
            }
        } catch (e) {
            antdMessage('error', '查询异常，请稍后重试');
            // 异常也当作失败处理，展示红色提示
            setQueryErrorMsg('查询异常，请稍后重试');
            updateQueried(false);
            setIsOldSystemData(undefined);
            setQueriedAsBound(false);
            // 👇 新增：异常时清空姓名
            setQueriedMemberName(undefined);
        } finally {
            setQuerying(false);
        }
    };

    let extendProps: WebEditUniversalProps = {
        ...props,
        form,
        apiId,
        serviceId: 'business',
        titleName: '绑定会员实体卡',
        isEnter: false,
        displayOptions: {
            width: 1080,
            footer: ({ onCancel }: any) => (
                <Space>
                    <Button type='primary' onClick={() => bindPhysicalCard()}>
                        绑定实体卡
                    </Button>
                    <Button onClick={onCancel}>取消</Button>
                </Space>
            )
        },
        formOptions: {
            instance: form,
            children: (
                <>
                    {/* 让“余额/赠送”里的数字也显示成红色 */}
                    <style>{`
                        .bind-card-important .ant-input-number-input {
                            color: #ff4d4f;
                            font-weight: 600;
                        }
                    `}</style>

                    {/* ============ 第一步：选择绑定方式 ============ */}
                    <Divider titlePlacement='start' styles={dividerTitleStyles}>
                        第一步：选择绑定方式
                    </Divider>
                    <Row gutter={[8, 0]} style={{ marginBottom: 12 }}>
                        <Col span={8}>
                            <Form.Item
                                label="绑定方式"
                                name="bindMethodId"
                                tooltip="绑定方式，是否关联手机注册会员？"
                                rules={[{ required: true, message: '请选择绑定方式' }]}>
                                <Select
                                    placeholder="请选择绑定方式"
                                    onChange={handleBindMethodChange}
                                    showSearch={{
                                        optionFilterProp: 'children',
                                        filterOption: (input, option: any) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }}>
                                    {getList.general(bindMethodData)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label="查询旧系统并转换"
                                name="isQueryAndChange"
                                valuePropName="checked"
                                tooltip="查询旧系统相关数据，并转换数据"
                                initialValue={true}>
                                <Checkbox disabled={queried} />
                            </Form.Item>
                        </Col>

                        {/* 新增：重置按钮，清空整个表单回到初始状态（小尺寸、靠右） */}
                        <Col
                            span={8}
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'flex-start'
                            }}
                        >
                            <Form.Item label=" " colon={false} style={{ marginBottom: 0 }}>
                                {/* 中文提示：鼠标悬浮显示说明 */}
                                <Tooltip title="清空整个表单，恢复到初始状态">
                                    <Button
                                        danger
                                        onClick={handleReset}
                                    >
                                        重置
                                    </Button>
                                </Tooltip>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* ============ 第二步：输入会员号、实体卡id 并查询 ============ */}
                    <Divider titlePlacement='start' styles={dividerTitleStyles}>
                        第二步：输入会员号、实体卡id 并查询
                    </Divider>
                    <Row gutter={[8, 0]} style={{ marginBottom: 12 }}>
                        <Col span={10}>
                            {/* 使用 Space.Compact 组合选择器和输入框，实现二选一查询 */}
                            <Form.Item label="查询条件" required={!boundDisabled} style={{ marginBottom: 0 }}>
                                <Space.Compact style={{ width: '100%' }}>
                                    <Form.Item name="queryType" noStyle initialValue="membershipId">
                                        <Select 
                                            style={{ width: 100 }} 
                                            disabled={boundDisabled || queried}
                                            onChange={(val) => {
                                                // 切换查询类型时，清空另一个字段的值和校验错误
                                                const clearField = val === 'membershipId' ? 'contactNumber' : 'membershipId';
                                                form.setFields([{ name: clearField, value: undefined, errors: [] }]);
                                            }}
                                        >
                                            <Select.Option value="membershipId">会员号</Select.Option>
                                            {/* 只有开关开启时，才渲染手机号选项 */}
                                            {ENABLE_PHONE_QUERY && (
                                                <Select.Option value="contactNumber">手机号</Select.Option>
                                            )}
                                        </Select>
                                    </Form.Item>
                                    <Form.Item
                                        noStyle
                                        shouldUpdate={(prev, curr) => prev.queryType !== curr.queryType}
                                    >
                                        {({ getFieldValue }) => {
                                            // 获取查询类型，如果开关关闭，强制使用 membershipId 作为渲染依据
                                            const rawQueryType = getFieldValue('queryType') || 'membershipId';
                                            const finalQueryType = ENABLE_PHONE_QUERY ? rawQueryType : 'membershipId';

                                            return (
                                                <Form.Item
                                                    name={finalQueryType}
                                                    noStyle
                                                    rules={[{ required: !boundDisabled, message: `请输入${finalQueryType === 'membershipId' ? '会员号' : '手机号'}` }]}
                                                >
                                                    <Input 
                                                        placeholder={`请输入${finalQueryType === 'membershipId' ? '会员号' : '手机号'}`} 
                                                        disabled={boundDisabled || queried} 
                                                    />
                                                </Form.Item>
                                            );
                                        }}
                                    </Form.Item>
                                </Space.Compact>
                            </Form.Item>
                        </Col>

                        <Col span={10}>
                            <Form.Item
                                label="实体卡id"
                                tooltip='会员绑定的实体卡id'
                                name="physicalCardId"
                                rules={[{ required: true, message: '请输入实体卡id' }]}>
                                <Input
                                    placeholder="请输入实体卡id"
                                    disabled={queried}
                                />
                            </Form.Item>
                        </Col>

                        {/* 查询按钮独立一列，同时查询会员卡 + 实体卡 */}
                        <Col span={4}>
                            <Form.Item label=" " colon={false}>
                                <Button
                                    type='primary'
                                    loading={querying}
                                    onClick={handleQuery}
                                    disabled={queried}
                                    block
                                >
                                    查询
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* 第二步反馈信息：左侧对齐“查询条件”，右侧对齐“实体卡id” */}
                    {(queryErrorMsg || queriedAsBound || (queried && isOldSystemData !== undefined)) && (
                        <Row gutter={[8, 0]} style={{ marginBottom: 12, marginTop: -4 }}>
                            {/* 左侧（对齐“查询条件”）：查询失败红色提示 或 查询到会员绿色提示 */}
                            <Col span={10}>
                                {queryErrorMsg ? (
                                    <span style={{ color: '#ff4d4f', fontSize: 13 }}>
                                        {queryErrorMsg}
                                    </span>
                                ) : (
                                    queriedAsBound && (
                                        <span style={{ color: '#52c41a', fontSize: 13, fontWeight: 600 }}>
                                            {/* 👇 修改这里：拼接查到的会员姓名 */}
                                            查询到会员{queriedMemberName ? `，姓名：${queriedMemberName}` : ''}
                                        </span>
                                    )
                                )}
                            </Col>

                            {/* 右侧（对齐“实体卡id”）：查询结果标识 */}
                            <Col span={14}>
                                {queried && isOldSystemData !== undefined && !queryErrorMsg && (
                                    <>
                                        <span style={{ color: '#f6ff00', marginRight: 8, fontSize: 13 }}>查询结果：</span>
                                        {isOldSystemData ? (
                                            <Tag color="blue" style={{ fontSize: 12, padding: '1px 10px' }}>
                                                旧系统转入
                                            </Tag>
                                        ) : (
                                            <Tag color="green" style={{ fontSize: 12, padding: '1px 10px' }}>
                                                新卡（旧系统中未查到资料）
                                            </Tag>
                                        )}
                                    </>
                                )}
                            </Col>
                        </Row>
                    )}

                    {/* ============ 第三步：输入或确认相关资料并绑定 ============ */}
                    <Divider titlePlacement='start' styles={dividerTitleStyles}>
                        第三步：输入或者确认相关资料并绑定
                    </Divider>

                    {/* 未查询成功时给用户一个明确提示 */}
                    {!queried && (
                        <div style={{ color: '#faad14', marginBottom: 12, fontSize: 13 }}>
                            请先在上方输入并点击“查询”，查询成功后即可填写下方资料。
                        </div>
                    )}

                    {/* 未查询成功时禁用第三步所有字段 */}
                    <fieldset
                        disabled={!queried}
                        style={{ border: 'none', padding: 0, margin: 0 }}
                    >
                        <Row gutter={[8, 0]}>
                            {/* ---- 第 1 行：类型 / 级别 / 验证方法 / 姓名 ---- */}
                            <Col span={6}>
                                <Form.Item
                                    label="类型"
                                    name="membershipTypeId"
                                    tooltip='会员类型'
                                    rules={[{ required: boundDisabled && queried, message: '请选择会员类型' }]}>
                                    <Select
                                        placeholder="会员类型"
                                        disabled={!boundDisabled}
                                        onChange={(e) => {
                                            // 类型变化时清空级别，并重新拉取级别列表
                                            form.setFieldValue('membershipLevelId', null);
                                            getMembershipLevelData(e, defaultService, setMembershipLevelData);
                                        }}
                                        showSearch={{
                                            optionFilterProp: 'children',
                                            filterOption: (input, option: any) =>
                                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }}>
                                        {getList.general(membershipTypeData, { itemName: 'typeName' })}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={6}>
                                <Form.Item
                                    label="级别"
                                    name="membershipLevelId"
                                    tooltip='会员级别'
                                    rules={[{ required: boundDisabled && queried, message: '请选择会员级别' }]}>
                                    <Select
                                        placeholder="会员级别"
                                        disabled={!boundDisabled}
                                        showSearch={{
                                            optionFilterProp: 'children',
                                            filterOption: (input, option: any) =>
                                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }}>
                                        {getList.general(membershipLevelData, { itemName: 'levelName' })}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={6}>
                                <Form.Item
                                    label="验证"
                                    name="verificationMethodId"
                                    rules={[{ required: boundDisabled && queried, message: '请选择验证方法' }]}>
                                    <Select
                                        placeholder="验证方法"
                                        disabled={!boundDisabled}
                                        showSearch={{
                                            optionFilterProp: 'children',
                                            filterOption: (input, option: any) =>
                                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }}>
                                        {getList.general(verificationMethodData)}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={6}>
                                <Form.Item
                                    label="姓名"
                                    name="membershipName"
                                    rules={[{ required: boundDisabled && queried, message: '请输入姓名!' }]}>
                                    <Input placeholder="请输入姓名" disabled={!boundDisabled} />
                                </Form.Item>
                            </Col>

                            {/* ---- 第 2 行：称呼 / 密码 / 性别 / 手机 ---- */}
                            <Col span={6}>
                                <Form.Item
                                    label="称呼"
                                    name="appellationId">
                                    <Select
                                        placeholder="称呼"
                                        disabled={!boundDisabled}
                                        showSearch={{
                                            optionFilterProp: 'children',
                                            filterOption: (input, option: any) =>
                                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }}>
                                        {getList.general(appellationData)}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={6}>
                                <Form.Item
                                    label="密码"
                                    name="password">
                                    <Input.Password placeholder="请输入密码" disabled={!boundDisabled} />
                                </Form.Item>
                            </Col>

                            <Col span={6}>
                                <Form.Item
                                    label="性别"
                                    name="gender">
                                    <Select
                                        placeholder="性别"
                                        disabled={!boundDisabled}
                                        showSearch={{
                                            optionFilterProp: 'children',
                                            filterOption: (input, option: any) =>
                                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }}>
                                        {getList.general([{ id: 1, name: '男' }, { id: 0, name: '女' }])}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={6}>
                                <Form.Item
                                    label="手机"
                                    name="contactNumber"
                                    tooltip='用户手机号，多平台关联用，唯一值（可留空）'>
                                    <Input placeholder="请输入手机号" disabled={!boundDisabled} />
                                </Form.Item>
                            </Col>

                            {/* ---- 第 3 行 ---- */}
                            <Col span={6}>
                                <Form.Item
                                    label="电话"
                                    name="phoneNumber"
                                    tooltip='固定电话或其他联系电话'>
                                    <Input placeholder="请输入电话号码" disabled={!boundDisabled} />
                                </Form.Item>
                            </Col>

                            <Col span={4}>
                                <Form.Item
                                    label="生日"
                                    name="dateOfBirth">
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        placeholder="请选择生日"
                                        disabled={!boundDisabled}
                                        format="YYYY-MM-DD"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={7}>
                                <Form.Item
                                    label="商户"
                                    name="merchantId"
                                    rules={[{ required: boundDisabled && queried, message: '请选择商户!' }]}>
                                    <Select
                                        placeholder="商户"
                                        disabled={!boundDisabled}
                                        showSearch={{
                                            optionFilterProp: 'children',
                                            filterOption: (input, option: any) =>
                                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }}>
                                        {getList.general(merchantData, { itemName: 'merchantName' })}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={7}>
                                <Form.Item
                                    label="店铺"
                                    name="shopId"
                                    rules={[{ required: boundDisabled && queried, message: '请选择店铺!' }]}>
                                    <Select
                                        placeholder="店铺"
                                        disabled={!boundDisabled}
                                        showSearch={{
                                            optionFilterProp: 'children',
                                            filterOption: (input, option: any) =>
                                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }}>
                                        {getList.general(shopData, { itemName: 'shopName' })}
                                    </Select>
                                </Form.Item>
                            </Col>

                            {/* ---- 第 4 行 ---- */}
                            <Col span={5}>
                                <Form.Item
                                    label="国籍"
                                    name="nationality"
                                    tooltip='会员的国籍'>
                                    <Input placeholder="请输入国籍" disabled={!boundDisabled} />
                                </Form.Item>
                            </Col>

                            <Col span={5}>
                                <Form.Item
                                    label="民族"
                                    name="ethnicity"
                                    tooltip='会员的民族'>
                                    <Input placeholder="请输入民族" disabled={!boundDisabled} />
                                </Form.Item>
                            </Col>

                            <Col span={6}>
                                <Form.Item
                                    label="身份证"
                                    name="idNumber">
                                    <Input placeholder="请输入身份证号" disabled={!boundDisabled} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item
                                    label="公司名"
                                    name="companyName">
                                    <Input placeholder="请输入公司名称" disabled={!boundDisabled} />
                                </Form.Item>
                            </Col>

                            {/* ---- 第 5 行：余额 / 赠送 / 地址 ---- */}
                            <Col span={4}>
                                <Form.Item
                                    // 红色加粗的 label
                                    label={<span style={importantLabelStyle}>余额</span>}
                                    name="balance">
                                    <InputNumber
                                        disabled={true}
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={4}>
                                <Form.Item
                                    // 红色加粗的 label
                                    label={<span style={importantLabelStyle}>赠送</span>}
                                    name="gifts">
                                    <InputNumber
                                        disabled={true}
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    label="地址"
                                    name="address">
                                    <Input placeholder="请输入详细地址" disabled={!boundDisabled} />
                                </Form.Item>
                            </Col>

                            <Col span={4}>
                                <Form.Item
                                    label="邮政编码"
                                    name="postalCode">
                                    <Input placeholder="请输入邮政编码" disabled={!boundDisabled} />
                                </Form.Item>
                            </Col>

                            {/* ---- 第 6 行：备注 ---- */}

                            <Col span={12}>
                                <Form.Item
                                    label="会员标签"
                                    name="tags"
                                    tooltip="多个标签用英文逗号分隔，例如：VIP,高频,已认证" >
                                    <Input disabled={!boundDisabled} placeholder="例如：VIP,高频,已认证" />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    label="备注"
                                    name="memo">
                                    <Input.TextArea
                                        rows={1}
                                        placeholder="请输入备注"
                                        style={{ resize: 'none' }}
                                        disabled={!boundDisabled}
                                    />
                                </Form.Item>
                            </Col>

                            {/* 隐藏字段：用于提交后台所需的各类ID */}
                            <Col span={0}>
                                <Form.Item name="accountId" hidden={true}>
                                    <Input />
                                </Form.Item>
                            </Col>

                            {/* 新增：隐藏的会员号，确保提交时包含此参数 */}
                            <Col span={0}>
                                <Form.Item name="membershipId" hidden={true}>
                                    <Input />
                                </Form.Item>
                            </Col>

                            <Col span={0}>
                                <Form.Item name="isQueryOldSystemSuccess" hidden={true}>
                                    <Checkbox />
                                </Form.Item>
                            </Col>

                        </Row>
                    </fieldset>
                </>
            )
        }
    };

    return (
        <>
            <WebEditUniversal ref={ref} {...extendProps} />
            {/* Modal.useModal 的 contextHolder：确保 modal.confirm 继承主题背景色（暗色深底 / 浅色白底） */}
            {contextHolder}
        </>
    )
}

export default EditComponent;